// src/pages/Home.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  Wallet,
  TrendingUp,
  Lock,
  Users,
  ArrowRight,
  CheckCircle,
  BarChart,
  Globe,
  Sparkles,
  ShieldCheck,
  Zap,
  Key,
  Cpu,
  Brain,
  AlertTriangle,
  Database,
  Network,
  Clock,
  FileText,
  Search,
  MessageSquare,
  UserCheck,
  Scale,
  Server,
  Target,
  Eye,
  RefreshCw,
  ShieldOff,
  BarChart4,
  Globe2,
  ShieldPlus,
  Coins,
  ZapOff,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const Home: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const heroTexts = [
    {
      main: "Professional Crypto Recovery",
      highlight: "Trusted by Thousands",
      color: "from-primary to-primary/80",
    },
    {
      main: "Expert Asset Recovery",
      highlight: "When Security Fails",
      color: "from-blue-500 to-blue-400",
    },
    {
      main: "Multi-Chain Protection",
      highlight: "For Modern Portfolios",
      color: "from-emerald-500 to-emerald-400",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setFade(true);
      }, 1500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: "ease-out-cubic",
      once: false,
    });
  }, []);

  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 overflow-hidden">
      {/* Navigation */}
      <PublicNavbar currentPage="/" />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 pb-20 lg:pt-28 lg:pb-24">
        {/* Animated Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-emerald-500/3 rounded-full blur-3xl animate-pulse"></div>

        <div className="container mx-auto px-4 lg:px-5 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div data-aos="fade-right" data-aos-delay="100">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
                <ShieldCheck className="h-4 w-4 mr-2" />
                Professional Crypto Recovery Services
              </div>

              {/* Rotating Hero Text */}
              <div className="h-48 lg:h-60 mb-8">
                <div
                  className={`transition-all duration-700 ${
                    fade
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-4"
                  }`}
                >
                  <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white leading-tight mb-6">
                    {heroTexts[currentTextIndex].main}
                    <span
                      className={`block bg-gradient-to-r ${heroTexts[currentTextIndex].color} bg-clip-text text-transparent`}
                    >
                      {heroTexts[currentTextIndex].highlight}
                    </span>
                  </h1>
                </div>
              </div>

              <p
                className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-lg"
                data-aos="fade-right"
                data-aos-delay="200"
              >
                Pelivor Recovery specializes in recovering lost or compromised
                cryptocurrency assets with expert forensic analysis and
                professional recovery services across all major blockchain
                networks.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <Link
                  to="/register"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} group/cta`}
                >
                  <span>Start Recovery Process</span>
                  <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover/cta:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonLarge} group/about`}
                >
                  <Sparkles className="h-5 w-5 mr-2 opacity-0 group-hover/about:opacity-100 transition-opacity" />
                  <span>Learn About Our Process</span>
                </Link>
              </div>
            </div>

            {/* Right Content - Feature Cards */}
            <div className="relative" data-aos="fade-left" data-aos-delay="400">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-primary/2 to-transparent rounded-3xl blur-xl opacity-50"></div>

              {/* Main Feature Card Container */}
              <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-6 lg:p-8">
                <div className="space-y-6">
                  {/* Blockchain Forensic Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-br from-primary/5 to-primary/2 border border-primary/20 group/feature hover:scale-[1.02] transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="100"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-primary/20 group-hover/feature:scale-110 transition-transform">
                        <Search className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            Blockchain Forensic Analysis
                          </h3>
                          <Eye className="h-4 w-4 text-primary opacity-0 group-hover/feature:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Expert tracing and analysis of blockchain transactions
                          to identify recovery opportunities and track
                          compromised assets across networks.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20">
                            Transaction Tracing
                          </span>
                          <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20">
                            Address Analysis
                          </span>
                          <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20">
                            Pattern Recognition
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Asset Recovery Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-br from-blue-500/5 to-blue-500/2 border border-blue-500/20 group/feature hover:scale-[1.02] transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-blue-500/20 group-hover/feature:scale-110 transition-transform">
                        <RefreshCw className="h-6 w-6 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            Professional Asset Recovery
                          </h3>
                          <ShieldPlus className="h-4 w-4 text-blue-500 opacity-0 group-hover/feature:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Systematic recovery of lost, stolen, or inaccessible
                          cryptocurrency assets through legal channels and
                          technical solutions.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-500 rounded-full border border-blue-500/20">
                            Legal Coordination
                          </span>
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-500 rounded-full border border-blue-500/20">
                            Technical Recovery
                          </span>
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-500 rounded-full border border-blue-500/20">
                            Multi-Chain Support
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Security Audit Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-br from-emerald-500/5 to-emerald-500/2 border border-emerald-500/20 group/feature hover:scale-[1.02] transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-emerald-500/20 group-hover/feature:scale-110 transition-transform">
                        <ShieldOff className="h-6 w-6 text-emerald-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            Security Vulnerability Audit
                          </h3>
                          <AlertTriangle className="h-4 w-4 text-emerald-500 opacity-0 group-hover/feature:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Comprehensive security assessments to identify
                          vulnerabilities and prevent future asset loss through
                          improved security practices.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded-full border border-emerald-500/20">
                            Risk Assessment
                          </span>
                          <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded-full border border-emerald-500/20">
                            Security Review
                          </span>
                          <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded-full border border-emerald-500/20">
                            Prevention Planning
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-20" ref={statsRef}>
        <div className="container mx-auto px-4 lg:px-5">
          <div className="text-center mb-12 lg:mb-16" data-aos="fade-up">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Recovery Success by Numbers
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Proven results in recovering lost cryptocurrency assets
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                value: 42,
                suffix: "M+",
                prefix: "$",
                label: "Assets Recovered",
                desc: "Total value retrieved",
                color: "text-primary",
                icon: Coins,
                delay: 100,
              },
              {
                value: 78,
                suffix: "%",
                label: "Success Rate",
                desc: "Recovery success rate",
                color: "text-emerald-500",
                icon: TrendingUp,
                delay: 200,
              },
              {
                value: 25,
                suffix: "+",
                label: "Blockchain Networks",
                desc: "Supported networks",
                color: "text-blue-500",
                icon: Network,
                delay: 300,
              },
              {
                value: 1500,
                suffix: "+",
                label: "Cases Handled",
                desc: "Successful recoveries",
                color: "text-purple-500",
                icon: Users,
                delay: 400,
              },
            ].map((stat, idx) => (
              <div
                key={idx}
                className={`${GlobalStyles.card} !p-6 text-center group/stat`}
                data-aos="zoom-in"
                data-aos-delay={stat.delay}
              >
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center group-hover/stat:scale-110 transition-transform">
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <div
                  className={`text-3xl lg:text-4xl font-bold ${stat.color} mb-2 group-hover/stat:scale-105 transition-transform`}
                >
                  {statsInView && (
                    <CountUp
                      end={stat.value}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                      duration={2.5}
                    />
                  )}
                </div>
                <div className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  {stat.label}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {stat.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-900/30">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="text-center mb-12 lg:mb-16" data-aos="fade-up">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Recovery Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comprehensive solutions for cryptocurrency asset recovery and
              protection
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Forensic Analysis Service */}
            <div
              className={`${GlobalStyles.card} backdrop-blur-xl group/service`}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl blur-lg"></div>
                <div className="relative w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/service:scale-110 transition-transform">
                  <Search className="h-7 w-7 text-primary" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Blockchain Forensic Analysis
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Expert tracing of blockchain transactions to identify recovery
                opportunities and track compromised assets across multiple
                networks.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Transaction chain analysis",
                  "Address clustering and tracking",
                  "Pattern recognition algorithms",
                  "Multi-chain correlation",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                <Link
                  to="/recovery"
                  className="text-primary hover:text-primary/80 font-medium text-sm inline-flex items-center group/link"
                >
                  Learn more
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Asset Recovery Service */}
            <div
              className={`${GlobalStyles.card} backdrop-blur-xl group/service`}
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-xl blur-lg"></div>
                <div className="relative w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/service:scale-110 transition-transform">
                  <RefreshCw className="h-7 w-7 text-blue-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Professional Asset Recovery
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Systematic recovery of lost, stolen, or inaccessible
                cryptocurrency assets through legal channels and technical
                solutions.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Legal coordination with authorities",
                  "Technical recovery procedures",
                  "Smart contract analysis",
                  "Wallet reconstruction",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                <Link
                  to="/recovery"
                  className="text-blue-500 hover:text-blue-600 font-medium text-sm inline-flex items-center group/link"
                >
                  Learn more
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Security Audit Service */}
            <div
              className={`${GlobalStyles.card} backdrop-blur-xl group/service`}
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 rounded-xl blur-lg"></div>
                <div className="relative w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/service:scale-110 transition-transform">
                  <ShieldOff className="h-7 w-7 text-emerald-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Security Vulnerability Audit
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Comprehensive security assessments to identify vulnerabilities
                and prevent future asset loss through improved security
                practices.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Security posture assessment",
                  "Risk identification and analysis",
                  "Best practices implementation",
                  "Continuous monitoring setup",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                <Link
                  to="/recovery"
                  className="text-emerald-500 hover:text-emerald-600 font-medium text-sm inline-flex items-center group/link"
                >
                  Learn more
                  <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-aos="fade-right">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Recovery Process
              </h2>
              <div className="space-y-6">
                <div className="flex items-start group/step">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/step:scale-110 transition-transform">
                    <span className="text-primary font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Initial Assessment
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Detailed review of your case, blockchain analysis, and
                      recovery potential assessment.
                    </p>
                  </div>
                </div>

                <div className="flex items-start group/step">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/step:scale-110 transition-transform">
                    <span className="text-blue-500 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Forensic Investigation
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Comprehensive blockchain forensic analysis to trace
                      transactions and identify recovery paths.
                    </p>
                  </div>
                </div>

                <div className="flex items-start group/step">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/step:scale-110 transition-transform">
                    <span className="text-emerald-500 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Recovery Execution
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Implementation of recovery strategy with legal
                      coordination and technical execution.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="relative" data-aos="fade-left">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-primary/2 to-transparent rounded-3xl blur-xl opacity-50"></div>
              <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Scale className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        Legal Compliance
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Full regulatory compliance in all jurisdictions we
                        operate
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        24/7 Expert Support
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Dedicated recovery team with rapid response times
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                      <ZapOff className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        No Recovery, No Fee
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        We only charge when we successfully recover your assets
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div
              className="relative bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 lg:p-12 text-center backdrop-blur-sm border border-primary/30"
              data-aos="fade-up"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Need Help Recovering Lost Crypto?
              </h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Contact our expert recovery team today for a free assessment of
                your case and discover how we can help retrieve your assets.
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <Link
                  to="/register"
                  className="bg-white text-primary hover:bg-gray-100 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center group/cta"
                >
                  <span>Start Recovery Process</span>
                  <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover/cta:translate-x-1" />
                </Link>
                <a
                  href="mailto:support@pelivorrecovery.org"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
                >
                  <Sparkles className="h-5 w-5 mr-2 inline-block" />
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="Pelivor Recovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  Pelivor Recovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Professional cryptocurrency recovery services specializing in
                forensic analysis and asset recovery across all major blockchain
                networks.
              </p>
              <div className="flex space-x-3">
                <Globe className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Users className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Lock className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Asset Recovery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/forensics"
                    className="hover:text-white transition-colors"
                  >
                    Forensic Analysis
                  </Link>
                </li>
                <li>
                  <Link
                    to="/security"
                    className="hover:text-white transition-colors"
                  >
                    Security Audits
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">support@pelivorrecovery.org</p>
              <p className="text-sm">24/7 Recovery Support</p>
              <p className="text-sm mt-2">pelivorrecovery.org</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} Pelivor Recovery. All rights
              reserved. Professional cryptocurrency recovery services.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
